import { useCallback, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { ReactFCC } from '@/types/react'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { useTranslate } from '@/utils/translate/translate-client'
import { useMultipartUpload } from '../../components/useMultipartUpload'
import { UploadContent } from './UploadContent'
import { UploadErrorType } from './UploadErrorMessage'
import { getAcceptAttribute, getValidationError, isValidFileType } from './uploadValidation'

interface UploadBoxProps {
  boxTitle?: string
  isActive?: boolean
  uploadedFileNames?: string[]
  acknowledged?: boolean
  maxSize?: number
  acceptedFileType: 'image' | 'video' | 'text'
  loadFiles: () => void
  type?: 'APPLICATION' | 'UPDATE' | 'PROJECT'
  assetId?: string
}

export const UploadBox: ReactFCC<UploadBoxProps> = ({
  acknowledged,
  maxSize,
  acceptedFileType,
  loadFiles,
  isActive = true,
  type,
  assetId,
  boxTitle,
}) => {
  const { t } = useTranslate('guild-create')
  const { query } = useSafeRouter()
  const inputRef = useRef<HTMLInputElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isUploadComplete, setIsUploadComplete] = useState(false)
  const [isError, setIsError] = useState<UploadErrorType>()
  const [file, setFile] = useState<File | null>(null)
  const [uploadMultipart, abortUpload, uploadProgress, isPreparingUpload] = useMultipartUpload({
    id: (query.id as string) ?? (query.projectId as string),
    type: type ?? 'APPLICATION',
    assetId,
    mime: acceptedFileType,
  })

  const resetFileInput = useCallback(() => {
    setFile(null)
    if (inputRef.current) inputRef.current.value = ''
  }, [])

  const handleSetIsError = useCallback(() => {
    const error = getValidationError(file, acceptedFileType, maxSize, t)
    if (error) setIsError(error)
  }, [file, acceptedFileType, maxSize, t])

  const handleFileInput = useCallback(
    async (newFile: File | null) => {
      if (!newFile) return

      const isValidType = isValidFileType(newFile, acceptedFileType)
      const isValidSize = !maxSize || newFile.size <= maxSize

      if (!isValidType || !isValidSize) {
        handleSetIsError()
        resetFileInput()
        return
      }

      setIsUploading(true)
      await uploadMultipart(newFile).catch((error) => {
        setIsError({
          type: t('uploadFailed', 'Upload failed'),
          message: error.message || t('unknownError', 'Unknown error'),
        })
        setIsUploading(false)
        return
      })
      setIsUploading(false)
      setIsUploadComplete(true)
      loadFiles()
      resetFileInput()
    },
    [acceptedFileType, maxSize, handleSetIsError, resetFileInput, uploadMultipart, loadFiles, t],
  )

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newFile = event.target.files?.[0] || null
    setFile(newFile)
    handleFileInput(newFile)
  }

  const canDrop = useMemo(() => !isUploading && acknowledged, [isUploading, acknowledged])
  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)

    const hasFiles = event.dataTransfer?.files?.length || event.dataTransfer?.items?.length

    if (canDrop && hasFiles) {
      const newFile = event.dataTransfer.files.item(0) ?? event.dataTransfer.items[0]?.getAsFile() ?? null
      setFile(newFile)
      handleFileInput(newFile)
    }
  }

  const handleClick = () => {
    if (canDrop) setFile(null)
  }

  const handleDragEvents = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    setIsDragging(false)
  }

  const handleAbort = () => {
    abortUpload()
    setIsUploading(false)
    resetFileInput()
  }

  const handleBrowseClick = () => {
    inputRef?.current?.click()
  }

  return (
    <div
      className={classNames(
        'flex w-full rounded-lg p-4 bg-white border-core-gray-300 items-center border-2 border-dashed justify-center',
        isDragging && !file && 'border-info-500 bg-info-50',
        isDragging && file && 'border-error-500 bg-error-100',
        isError && 'border-error-500 bg-error-100',
        !file ? 'cursor-pointer' : 'cursor-not-allowed',
        isUploading && 'bg-core-gray-100',
      )}
      onClick={isActive ? handleClick : undefined}
      onDragEnter={isActive ? handleDragEvents : undefined}
      onDragOver={isActive ? handleDragEvents : undefined}
      onDragLeave={isActive ? handleDragLeave : undefined}
      onDrop={isActive ? handleDrop : undefined}
    >
      <input
        type="file"
        className="hidden"
        ref={inputRef}
        onChange={handleInputChange}
        accept={getAcceptAttribute(acceptedFileType)}
      />
      <UploadContent
        acknowledged={acknowledged ?? false}
        isActive={isActive ?? true}
        isError={isError}
        setIsError={() => setIsError(undefined)}
        maxSize={maxSize ?? 0}
        file={file}
        uploadProgress={uploadProgress}
        isPreparingUpload={isPreparingUpload}
        isUploadComplete={isUploadComplete}
        handleAbort={handleAbort}
        handleBrowseClick={handleBrowseClick}
        boxTitle={boxTitle ?? ''}
        isUploading={isUploading}
        acceptedFileType={acceptedFileType}
        type={type ?? 'APPLICATION'}
      />
    </div>
  )
}
